import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { motion, useInView, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Beweging                                                           */
/* ------------------------------------------------------------------ */

export const EASE = [0.22, 1, 0.36, 1]; // snel weg, zacht landen
export const EASE_IN_OUT = [0.76, 0, 0.24, 1]; // gordijnen en wipes

export const CONTACT_MAIL =
   "mailto:info@resto-online.be?subject=Demo%20van%20Resto-Online&body=Hallo%20Resto-Online%2C%0A%0AIk%20wil%20graag%20een%20demo%20van%20een%20webshop%20voor%20mijn%20zaak.%0A%0AMet%20vriendelijke%20groeten%2C";
export const WHATSAPP = "https://wa.me/32468229087";

/* Lenis-instantie van de landingspagina, zodat de nav het scrollen kan pauzeren */
export const smoothScroll = { lenis: null };

export function scrollToTarget(target, offset = -24) {
   const el = typeof target === "string" ? document.querySelector(target) : target;
   if (!el) return;
   if (smoothScroll.lenis) smoothScroll.lenis.scrollTo(el, { offset, duration: 1.4 });
   else el.scrollIntoView({ behavior: "smooth", block: "start" });
}

/* Is de intro (het gordijn) klaar? De hero wacht hierop. */
export const IntroContext = createContext(true);
export const useIntroDone = () => useContext(IntroContext);

/* Desktop-layout (lg) of niet; volgt het venster */
export function useIsDesktop() {
   const query = "(min-width: 1024px)";
   const [is, setIs] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
   useEffect(() => {
      const mq = window.matchMedia(query);
      const on = () => setIs(mq.matches);
      mq.addEventListener("change", on);
      return () => mq.removeEventListener("change", on);
   }, []);
   return is;
}

export const euro = (v) =>
   new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR" }).format(v);

/* ------------------------------------------------------------------ */
/*  Tekst                                                              */
/* ------------------------------------------------------------------ */

/**
 * Regels die van achter een masker omhoog schuiven.
 * `play` stuurt de animatie zelf aan; zonder `play` start ze wanneer de tekst in beeld komt.
 */
export function MaskLines({ lines, className = "", lineClassName = "", delay = 0, stagger = 0.08, play, as: Tag = "div", ...rest }) {
   const ref = useRef(null);
   const inView = useInView(ref, { once: true, margin: "-12% 0px" });
   const show = play === undefined ? inView : play;
   return (
      <Tag ref={ref} className={className} {...rest}>
         {lines.map((line, i) => (
            <span key={i} className={`block overflow-hidden pb-[0.12em] -mb-[0.12em] ${lineClassName}`}>
               <motion.span
                  className="block will-change-transform"
                  initial={{ y: "115%", rotate: 2.5 }}
                  animate={show ? { y: "0%", rotate: 0 } : { y: "115%", rotate: 2.5 }}
                  transition={{ duration: 1.05, ease: EASE, delay: delay + i * stagger }}
               >
                  {line}
               </motion.span>
            </span>
         ))}
      </Tag>
   );
}

/* Zacht omhoog in beeld schuiven */
export function Reveal({ children, className = "", delay = 0, y = 28, as = "div" }) {
   const M = motion[as];
   return (
      <M
         className={className}
         initial={{ opacity: 0, y }}
         whileInView={{ opacity: 1, y: 0 }}
         viewport={{ once: true, margin: "-10% 0px" }}
         transition={{ duration: 0.9, ease: EASE, delay }}
      >
         {children}
      </M>
   );
}

/* "— Live demo" */
export function SectionTag({ children, dark = false, className = "" }) {
   return (
      <div
         className={`flex items-center gap-3 font-ticket text-[11px] font-medium uppercase tracking-[0.18em] ${
            dark ? "text-lp-paper/55" : "text-lp-muted"
         } ${className}`}
      >
         <span className="h-1.5 w-1.5 rounded-full bg-lp-blue" />
         <span>{children}</span>
      </div>
   );
}

/* Cursief, serif accentwoord in een kop */
export const Em = ({ children, className = "" }) => (
   <em className={`font-editorial font-normal italic tracking-[-0.01em] ${className}`}>{children}</em>
);

/* ------------------------------------------------------------------ */
/*  Cijfers                                                            */
/* ------------------------------------------------------------------ */

/** Getal dat naar zijn nieuwe waarde veert. `fromZero`: telt eerst op vanaf 0 wanneer het in beeld komt. */
export function AnimatedNumber({ value, format = (v) => Math.round(v).toString(), fromZero = false, className = "" }) {
   const ref = useRef(null);
   const inView = useInView(ref, { once: true, margin: "-10% 0px" });
   const mv = useSpring(fromZero ? 0 : value, { stiffness: 70, damping: 18, mass: 0.9 });
   const text = useTransform(mv, (v) => format(v));

   useEffect(() => {
      if (fromZero && !inView) return;
      mv.set(value);
   }, [value, inView, fromZero, mv]);

   return (
      <motion.span ref={ref} className={`tabular-nums ${className}`}>
         {text}
      </motion.span>
   );
}

/* ------------------------------------------------------------------ */
/*  Knoppen                                                            */
/* ------------------------------------------------------------------ */

/** Volgt de cursor een beetje, zoals een magneet */
export function Magnetic({ children, strength = 0.3, className = "" }) {
   const ref = useRef(null);
   const x = useSpring(0, { stiffness: 220, damping: 16, mass: 0.5 });
   const y = useSpring(0, { stiffness: 220, damping: 16, mass: 0.5 });

   const onMove = (e) => {
      if (e.pointerType && e.pointerType !== "mouse") return;
      const r = ref.current.getBoundingClientRect();
      x.set((e.clientX - (r.left + r.width / 2)) * strength);
      y.set((e.clientY - (r.top + r.height / 2)) * strength);
   };
   const reset = () => {
      x.set(0);
      y.set(0);
   };

   return (
      <motion.div ref={ref} onPointerMove={onMove} onPointerLeave={reset} style={{ x, y }} className={`inline-block ${className}`}>
         {children}
      </motion.div>
   );
}

/* Label dat bij hover omhoog doorrolt */
export const RollText = ({ children }) => (
   <span className="relative block overflow-hidden">
      <span className="block transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)] group-hover:-translate-y-full">
         {children}
      </span>
      <span
         aria-hidden
         className="absolute inset-0 block translate-y-full transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)] group-hover:translate-y-0"
      >
         {children}
      </span>
   </span>
);

const PILL_VARIANTS = {
   ink: "bg-lp-ink text-lp-paper",
   paper: "bg-lp-paper text-lp-ink",
   blue: "bg-lp-blue text-white",
   ghost: "border border-lp-ink/15 text-lp-ink",
   "ghost-dark": "border border-lp-paper/20 text-lp-paper",
};
const PILL_FILL = {
   ink: "bg-lp-blue",
   paper: "bg-lp-blue",
   blue: "bg-lp-ink",
   ghost: "bg-lp-ink",
   "ghost-dark": "bg-lp-paper",
};
// Staat op de knop zelf, dus hover: (group-hover: werkt enkel voor kinderen van .group)
const PILL_HOVER_TEXT = {
   ink: "hover:text-white",
   paper: "hover:text-white",
   blue: "hover:text-white",
   ghost: "hover:text-lp-paper",
   "ghost-dark": "hover:text-lp-ink",
};

/** Pilvormige knop/link: vulling die van onder opkomt, rollend label en een pijl */
export function PillButton({ href, onClick, children, variant = "ink", arrow = true, className = "", size = "md", as, ...rest }) {
   const Tag = as || (href ? "a" : "button");
   const pad = size === "lg" ? "pl-7 pr-2 py-2 text-base" : "pl-5 pr-1.5 py-1.5 text-[15px]";
   const dot = size === "lg" ? "h-11 w-11" : "h-9 w-9";
   return (
      <Tag
         href={href}
         onClick={onClick}
         className={`group relative inline-flex items-center gap-4 overflow-hidden rounded-full font-medium transition-colors duration-500 ${pad} ${PILL_VARIANTS[variant]} ${PILL_HOVER_TEXT[variant]} ${
            arrow ? "" : "!pr-5"
         } ${className}`}
         {...rest}
      >
         <span
            aria-hidden
            className={`absolute inset-0 origin-bottom scale-y-0 rounded-full transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-y-100 ${PILL_FILL[variant]}`}
         />
         <span className="relative">
            <RollText>{children}</RollText>
         </span>
         {arrow && (
            <span
               className={`relative flex ${dot} items-center justify-center rounded-full transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)] group-hover:rotate-45 ${
                  variant === "ink" || variant === "blue" ? "bg-white/15" : variant === "ghost-dark" ? "bg-lp-paper/10" : "bg-lp-ink/10 group-hover:bg-white/15"
               }`}
            >
               <ArrowUpRight size={18} />
            </span>
         )}
      </Tag>
   );
}

/* ------------------------------------------------------------------ */
/*  Kleine decoratie                                                   */
/* ------------------------------------------------------------------ */

/* Het "wifi-cloche"-symbool uit het logo, als lijnen die kunnen animeren */
export function ClocheMark({ className = "", animate = false, color = "currentColor", wave = "#1F5CFF" }) {
   const arc = (d, i) =>
      animate ? (
         <motion.path
            key={i}
            d={d}
            stroke={wave}
            strokeWidth="9"
            strokeLinecap="round"
            fill="none"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.15 + i * 0.18, ease: EASE }}
         />
      ) : (
         <path key={i} d={d} stroke={wave} strokeWidth="9" strokeLinecap="round" fill="none" />
      );
   return (
      <svg viewBox="0 0 120 110" className={className} fill="none" aria-hidden>
         {arc("M14 46 C 38 12, 82 12, 106 46", 0)}
         {arc("M30 58 C 46 38, 74 38, 90 58", 1)}
         <circle cx="60" cy="70" r="6" fill={color} />
         <path d="M18 104 C 18 80, 36 72, 60 72 C 84 72, 102 80, 102 104 Z" fill={color} />
      </svg>
   );
}

/* Zesarmige ster als scheidingsteken */
export const Asterisk = ({ className = "" }) => (
   <svg viewBox="0 0 40 40" className={className} aria-hidden>
      {[0, 30, 60, 90, 120, 150].map((r) => (
         <rect key={r} x="18" y="2" width="4" height="36" rx="2" fill="currentColor" transform={`rotate(${r} 20 20)`} />
      ))}
   </svg>
);
